import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { patch } from "../../lib/api";
import { OPERATOR_NAME_MAX, inviteFormIssues, lockoutReason } from "../../lib/operator";
import { sortPermissions } from "../../lib/permissions";
import { useSession } from "../../lib/session";
import type { Operator } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice, Notice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { MicroLabel } from "../ui/Text";
import { AccessPicker, useRoles } from "./AccessPicker";

/**
 * Changing an operator's name, or what they may do.
 *
 * **Two permissions, two halves.** Renaming is `operators.update`; roles and
 * direct permissions are `operators.assign_roles`. A half the actor lacks is not
 * drawn as an editable field: a name box that the server would refuse to save is
 * worse than a name printed plainly.
 *
 * Only what changed is sent: the server reads an absent key as "unchanged", and
 * restating a name nobody touched would put a no-op rename in the audit log beside
 * the access change that was the point. When access changes, roles and direct
 * permissions are sent together — both replace the current sets, so sending one
 * alone would be fine, but sending the pair means the request says exactly what
 * the form showed.
 *
 * An access change takes effect on the operator's next request, not their next
 * sign-in: the server resolves permissions from the account on every request.
 */
export function EditOperator({
  operator,
  operators,
  selfId,
  onSaved,
  onCancel,
}: {
  readonly operator: Operator;
  /** The whole list, for the lockout check — it is about who else is an Administrator. */
  readonly operators: readonly Operator[];
  readonly selfId: string | undefined;
  readonly onSaved: (operator: Operator) => void;
  readonly onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canRename = can("operators.update");
  const canChangeAccess = can("operators.assign_roles");
  const roles = useRoles(canChangeAccess);

  const [name, setName] = useState(operator.name);
  const [roleIds, setRoleIds] = useState<readonly string[]>(operator.roles.map((role) => role.id));
  const [permissions, setPermissions] = useState<readonly string[]>(operator.permissions);

  const save = useMutation({
    mutationFn: (body: {
      name?: string;
      roleIds?: readonly string[];
      permissions?: readonly string[];
    }) => patch<Operator>(`/operators/${operator.id}`, body),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
      onSaved(updated);
    },
  });

  const trimmed = name.trim();
  const nameChanged = canRename && trimmed !== operator.name;
  const accessChanged =
    canChangeAccess &&
    (sameSet(
      roleIds,
      operator.roles.map((role) => role.id),
    ) === false ||
      sameSet(sortPermissions(permissions), sortPermissions(operator.permissions)) === false);

  const issues = inviteFormIssues({ email: "", name, roleIds, permissions });
  const nextRoles = (roles.data ?? [])
    .filter((role) => roleIds.includes(role.id))
    .map(({ id, name: roleName, grantsAll }) => ({ id, name: roleName, grantsAll }));
  const lockout = accessChanged
    ? lockoutReason(operators, selfId, { kind: "access", id: operator.id, roles: nextRoles })
    : undefined;

  const canSubmit =
    (nameChanged || accessChanged) &&
    trimmed !== "" &&
    issues.name === undefined &&
    (!accessChanged || issues.access === undefined) &&
    lockout === undefined &&
    !save.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>Edit {operator.email}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {save.isError ? <ErrorNotice error={save.error} /> : null}
      {lockout === undefined ? null : <Notice tone="warn">{lockout}</Notice>}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          save.mutate({
            ...(nameChanged ? { name: trimmed } : {}),
            ...(accessChanged ? { roleIds, permissions } : {}),
          });
        }}
        className="flex flex-col gap-5"
      >
        {canRename ? (
          <Field
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={OPERATOR_NAME_MAX}
            required
            autoFocus
            className="max-w-md"
            {...(issues.name === undefined ? {} : { error: issues.name })}
          />
        ) : (
          <p className="m-0 text-body text-ink">{operator.name}</p>
        )}

        {canChangeAccess ? (
          <AccessPicker
            roleIds={roleIds}
            permissions={permissions}
            onRolesChange={setRoleIds}
            onPermissionsChange={setPermissions}
            error={accessChanged ? issues.access : undefined}
          />
        ) : (
          <p className="m-0 text-data text-ink-faint">
            Changing roles needs the Change operator access permission.
          </p>
        )}

        <div>
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={save.isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}
